import {ApplicationRef, ComponentRef, createComponent, Injectable, Injector, EnvironmentInjector} from '@angular/core';
import {ConfirmDialogComponent} from "../../modals/confirm-dialog/confirm-dialog.component";

export type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmBtnClass?: string; // 'btn-danger' | 'btn-primary' | etc.
  modalOptions?: {
    size?: 'sm' | 'md' | 'lg';
    backdrop?: 'static' | true;
  };
};

@Injectable({providedIn: 'root'})
export class ConfirmService {
  constructor(
    private injector: Injector,
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector
  ) {}

  async ask(opts: ConfirmOptions = {}): Promise<boolean> {
    // Create the component dynamically
    const componentRef: ComponentRef<ConfirmDialogComponent> = createComponent(ConfirmDialogComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector
    });

    const instance = componentRef.instance;

    // Set input properties
    instance.title = opts.title ?? 'Confirm';
    instance.message = opts.message ?? 'Are you sure?';
    instance.confirmText = opts.confirmText ?? 'Yes';
    instance.cancelText = opts.cancelText ?? 'No';
    instance.confirmBtnClass = opts.confirmBtnClass ?? 'btn-danger';
    instance.size = opts.modalOptions?.size ?? 'md';
    instance.backdrop = opts.modalOptions?.backdrop ?? 'static';

    // Attach the component to the Angular application
    this.appRef.attachView(componentRef.hostView);

    // Append to document body
    const domElem = componentRef.location.nativeElement;
    document.body.appendChild(domElem);

    // Trigger change detection to ensure ViewChild is initialized
    componentRef.changeDetectorRef.detectChanges();

    // Open the modal and wait for result
    try {
      return await instance.open();
    } finally {
      // Cleanup after modal closes
      setTimeout(() => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        domElem.remove();
      }, 300); // Small delay to allow close animation
    }
  }
}
